import { useContext, useCallback, useState } from 'react';
import _ from 'underscore';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { getLocalStorage } from '@bingads-webui/storage';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { useBrandKitDataService } from '@bingads-webui-campaign-react/brandkit-data-service';
import { formatBrandkitForAPI } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

const FetchCount = 10;

export const useVideoAdsCopilot = (isNativeAd) => {
  const [, setRecommendationContext] = useContext(RecommendationContext);
  const activity = useContext(ActivityContext);

  const repository = useCopilotRepositoryContext();

  const { logBannerGenerated } = useCopilotLoggers();

  const { fetchVideoAdsAssetRecommendations, finalUrl } = repository;
  const [isLoadingBrandKit, setIsLoadingBrandkit] = useState(false);
  const [brandkit, setBrandKit] = useState(null);
  const [isBrandKitEmpty, setIsBrandKitEmpty] = useState(true);
  const { scope } = useContext(ScopeContext) || {};

  const { aid } = scope.values;
  const permissions = useContext(PermissionsContext);
  // For Video BK will only work with new templates
  const isBrandKitEnabled = permissions && permissions.dynamic && (permissions.dynamic.BrandKitUI && permissions.dynamic.VideoTemplateUI);
  const isVideoTemplateEnabled = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;
  const isVideoAssetGenVerticalSizesEnabled = permissions && permissions.dynamic && permissions.dynamic.VideoAssetGenVerticalSizes;

  const { getBrandKitDataByAccId } = useBrandKitDataService();

  const fetchBrandKit = useCallback(async () => {
    try {
      if (isBrandKitEnabled) {
        setIsLoadingBrandkit(true);
        const response = await getBrandKitDataByAccId({ accId: aid });
        setIsLoadingBrandkit(false);
        const isBrandKitHavingData = response && response.BrandKits && response.BrandKits[0] && (!_.isEmpty(response.BrandKits[0].Fonts) ||
          !_.isEmpty(response.BrandKits[0].Images) ||
          !_.isEmpty(response.BrandKits[0].LandscapeLogos) ||
          !_.isEmpty(response.BrandKits[0].Palettes) ||
          !_.isEmpty(response.BrandKits[0].SquareLogos));
        if (isBrandKitHavingData) {
          setIsBrandKitEmpty(false);
          const updatedResponse = {
            ...response.BrandKits[0],
          };
          setBrandKit(formatBrandkitForAPI(updatedResponse));
        } else {
          setIsBrandKitEmpty(true);
        }
      }
    } catch (error) {
      if (activity && activity.error) {
        activity.error(`VideoAdsCopilot:VideoBrandkitError, ${error}`);
      }
    } finally {
      setIsLoadingBrandkit(false);
    }
  }, [isBrandKitEnabled, getBrandKitDataByAccId, aid, setBrandKit, setIsBrandKitEmpty]);

  const fetch = useCallback((taskManager, useBrandKit) => {
    if (!finalUrl || isLoadingBrandKit) {
      return;
    }

    setRecommendationContext({
      isLoading: true,
      errorMessage: undefined,
    });

    const templateFilter = isVideoTemplateEnabled ? {
      AspectRatios: ['16:9', ...(isVideoAssetGenVerticalSizesEnabled ? ['9:16'] : [])],
      Durations: [15, ...(isVideoAssetGenVerticalSizesEnabled ? [30] : [])],
    } : null;

    taskManager.addTask({
      taskId: isBrandKitEnabled ? `${finalUrl}-brandkit-${useBrandKit}` : finalUrl,
      taskFn: () => {
        setRecommendationContext({ isLoading: true });
        const localStorage = getLocalStorage();
        const campaignObjective = localStorage.get('CampaignObjective');
        const recommendationType = campaignObjective === 'Awareness' ? 'OLV' : 'CTV';
        return fetchVideoAdsAssetRecommendations(FetchCount, false, recommendationType, templateFilter, (useBrandKit && brandkit) ? brandkit : null);
      },
    }).then((response) => {
      const { ads } = response.data;

      const videoAssets = ads.map((ad) => {
        const {
          url, thumbnailUrl, recommendationId, template, videoMetadata,
        } = ad;

        return {
          url,
          thumbnailUrl,
          quality: videoMetadata.quality,
          width: videoMetadata.width,
          height: videoMetadata.height,
          sourceVideoBitRate: videoMetadata.sourceVideoBitRate,
          suggestionId: recommendationId,
          RecommendationId: recommendationId,
          template,
        };
      });

      // log the generated recommendations.
      if (activity && videoAssets && videoAssets.length > 0) {
        const recommendationDetails = {
          isNativeAd: !!isNativeAd,
          isVideoTemplateEnabled,
          isVideoAssetGenVerticalSizesEnabled,
          finalUrl,
          videoAssets: videoAssets.map(videoAsset => ({
            url: videoAsset.url,
            recommendationId: videoAsset.RecommendationId,
            templateId: videoAsset.template && videoAsset.template.templateId,
          })),
        };
        activity.trace(`VideoAdsCopilot:VideoRecommendationsGenerated, ${JSON.stringify(recommendationDetails)}`);
      }

      logBannerGenerated(finalUrl, {
        RecommendedAssets: {
          VideoAssets: videoAssets,
        },
      });

      setRecommendationContext({
        isLoading: false,
        finalUrl,
        videoAssets,
      });
    }).catch(() => {
      setRecommendationContext({
        isLoading: false,
        finalUrl,
        videoAssets: [],
        errorMessage: _TL_('Unable to generate recommendations for this Final URL'),
      });
    });
  }, [fetchVideoAdsAssetRecommendations, finalUrl, logBannerGenerated, setRecommendationContext, isLoadingBrandKit, brandkit]);

  return {
    fetch,
    finalUrlForCopilot: finalUrl,
    isBrandKitEmpty,
    fetchBrandKit,
  };
};
